import React, { Component, ReactNode } from "react";
import { Error500 } from "@/pages";
import {
  isAbortedRequestError,
  sendSlackMessage,
  SLACK_CHANNELS,
} from "@/utils";
import { node } from "prop-types";

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
  info: any;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      info: null,
    };
  }

  async componentDidMount() {
    // Global error handler for uncaught errors
    window.addEventListener("error", this.handleGlobalError);

    // Global handler for unhandled promise rejections
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection,
    );

    // Override the default console.error behavior
    if (import.meta.env.VITE_NODE_ENV === "production") {
      // eslint-disable-next-line no-console
      const originalConsoleError = console.error;
      // eslint-disable-next-line no-console
      console.error = function (...args) {
        if (args.some((arg) => isAbortedRequestError(arg))) {
          return;
        }
        const message = args.join(" ").split("\n").join("\\n");

        const slackErrorData = {
          channel: SLACK_CHANNELS.FRONTEND_ERRORS,
          type: "error",
          heading: window.location.href,
          detail: message,
          fields: [] as Array<{
            title: string;
            value: string;
            short?: boolean;
          }>,
        };
        sendSlackMessage(slackErrorData).then();

        originalConsoleError.apply(console, args);
      };
    }
  }

  componentWillUnmount() {
    // Clean up event listeners
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection,
    );
  }

  handleGlobalError = (event: ErrorEvent) => {
    if (isAbortedRequestError(event.error || event.message)) {
      event.preventDefault();
      return;
    }

    // Handle global JavaScript errors
    if (import.meta.env.VITE_NODE_ENV === "production") {
      const errorMessage =
        event.error?.stack || event.message || "Unknown error";
      const slackErrorData = {
        channel: SLACK_CHANNELS.FRONTEND_ERRORS,
        type: "error",
        heading: `Global Error: ${window.location.href}`,
        detail: errorMessage.split("\n").join("\\n"),
        fields: [
          {
            title: "Error Type",
            value: event.error?.name || "Error",
            short: true,
          },
          {
            title: "File",
            value: event.filename || "Unknown",
            short: true,
          },
          {
            title: "Line:Column",
            value: `${event.lineno || "?"}:${event.colno || "?"}`,
            short: true,
          },
        ],
      };
      sendSlackMessage(slackErrorData).then();
    }

    // Prevent the default browser error handling
    event.preventDefault();
  };

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    if (isAbortedRequestError(event.reason)) {
      event.preventDefault();
      return;
    }

    // Handle unhandled promise rejections
    if (import.meta.env.VITE_NODE_ENV === "production") {
      const reason =
        event.reason?.stack ||
        event.reason?.message ||
        event.reason ||
        "Unknown rejection";
      const rejectionReason =
        typeof event.reason === "string"
          ? event.reason
          : event.reason?.message || "Unknown rejection";
      const slackErrorData = {
        channel: SLACK_CHANNELS.FRONTEND_ERRORS,
        type: "error",
        heading: `Unhandled Promise Rejection: ${window.location.href}`,
        detail:
          typeof reason === "string"
            ? reason.split("\n").join("\\n")
            : JSON.stringify(reason),
        fields: [
          {
            title: "Rejection Reason",
            value: rejectionReason,
            short: false,
          },
        ],
      };
      sendSlackMessage(slackErrorData).then();
    }

    // Prevent the default browser handling
    event.preventDefault();
  };

  async componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (isAbortedRequestError(error)) {
      return;
    }

    this.setState({
      hasError: true,
    });

    this.setState({
      error: error,
      info: info,
    });

    if (import.meta.env.VITE_NODE_ENV === "production") {
      const slackErrorData = {
        channel: SLACK_CHANNELS.FRONTEND_ERRORS,
        type: "error",
        heading: window.location.href,
        detail: error.toString().split("\n").join("\\n"),
        fields: [] as Array<{ title: string; value: string; short?: boolean }>,
      };
      sendSlackMessage(slackErrorData).then();
    }
  }

  render() {
    if (this.state.hasError) {
      if (import.meta.env.VITE_NODE_ENV !== "production") {
        return (
          <>
            <h1>Something went wrong. {import.meta.env.VITE_NODE_ENV}</h1>
            <pre>{this.state.error.stack}</pre>
            <hr />
            <h2>Full Stack</h2>
            <pre>{this.state.info.componentStack}</pre>
          </>
        );
      } else {
        return <Error500 />;
      }
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: node.isRequired,
};

// export ErrorBoundary;
